import { NgFor, NgIf } from '@angular/common';
import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { startWith } from 'rxjs';

interface IRegistrationScreen {
  label: string;
  /** Controle do grupo de cadastro; sem ele, a tela é sempre pedida. */
  control?: 'phone';
  /** Por que a tela é obrigatória (tooltip do cadeado). */
  reason?: string;
}

/** Identificação no início do fluxo do totem (OE-1876), usada no cadastro e na edição do setup. */
@Component({
  selector: 'app-setup-identification',
  standalone: true,
  imports: [NgIf, NgFor, ReactiveFormsModule, MatIconModule, MatTooltipModule],
  templateUrl: './setup-identification.component.html',
  styleUrls: ['./setup-identification.component.scss']
})
export class SetupIdentificationComponent implements OnInit {
  @Input({ required: true }) anonymousControl!: UntypedFormControl;
  @Input({ required: true }) registrationGroup!: UntypedFormGroup;
  /** Módulos do setup que pedem idade e sexo; o pai manda um array novo quando os módulos mudam. */
  @Input() set ageGenderModuleNames(names: string[] | null) {
    this.ageGenderModulesText = (names ?? []).join(', ');
  }

  // Ordem das telas no totem.
  readonly screens: IRegistrationScreen[] = [
    { label: 'CPF', reason: 'Identifica a pessoa e evita cadastro duplicado.' },
    { label: 'Nome completo', reason: 'Identifica a pessoa nos resultados.' },
    { label: 'Nascimento', reason: 'A idade entra no cálculo dos indicadores de saúde.' },
    { label: 'Celular', control: 'phone' },
    { label: 'E-mail', reason: 'É o login do totem e o envio dos resultados.' },
    { label: 'Sexo biológico', reason: 'O sexo entra no cálculo dos indicadores de saúde.' },
    { label: 'Termos', reason: 'Registra o consentimento.' }
  ];

  ageGenderModulesText = '';
  anonymousAllowed = false;
  registrationEnabled = true;
  phoneEnabled = true;
  /** Posição de cada tela no totem; null na tela tirada do cadastro. */
  screenNumbers: (number | null)[] = [];

  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.anonymousControl.valueChanges
      .pipe(startWith(this.anonymousControl.value), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => (this.anonymousAllowed = value === true));

    // Sem cadastro, as telas opcionais não se aplicam: ficam desabilitadas, com o valor guardado.
    const enabled = this.registrationGroup.get('enabled');
    const phone = this.registrationGroup.get('phone');
    enabled?.valueChanges.pipe(startWith(enabled.value), takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.registrationEnabled = value === true;
      if (this.registrationEnabled && phone?.disabled) phone.enable({ emitEvent: false });
      if (!this.registrationEnabled && phone?.enabled) phone.disable({ emitEvent: false });
    });
    phone?.valueChanges.pipe(startWith(phone.value), takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.phoneEnabled = value === true;
      let position = 0;
      this.screenNumbers = this.screens.map((screen) => (screen.control && !this.phoneEnabled ? null : ++position));
    });
  }

  /** Liga ou desliga uma tela opcional do cadastro pela pill. */
  toggleScreen(screen: IRegistrationScreen): void {
    if (!screen.control || !this.registrationEnabled) return;
    const control = this.registrationGroup.get(screen.control);
    control?.setValue(!control.value);
    control?.markAsDirty();
  }
}
